import {
  capitalizeWordsInSentence,
  CreditApplicationFieldsValues,
  formatMoney,
  getStateAbbreviation,
  getStateName,
} from '@dill/dill-shared';
import { getSamplSOS, getSampleTin } from './middeskData';

/**
 * Shared fraud/verification-signal helpers used by both the inline field-level
 * verification UI (PreviewCreditApplicationData) and the aggregated
 * CreditApplicationSummaryStep. Keeping this logic in one place means the
 * Summary step can't silently drift out of sync with what's shown inline.
 */

export const NON_US_LOCATION_FLAG = 'Applied from outside the USA';

/**
 * Flags when a signer's IP-derived location is outside the USA, or in a
 * different state than the company's registered address.
 */
export const getIpLocationFlag = (
  location: string | undefined,
  companyStateAbbr: string | null
): string | null => {
  if (!location) return null;
  const parts = location.split(',').map((p) => p.trim());
  const country = parts[parts.length - 1] || '';
  const region = parts.length >= 2 ? parts[parts.length - 2] : '';
  const isUSA = ['united states', 'united states of america', 'usa', 'us'].includes(
    country.toLowerCase()
  );
  if (!isUSA) return NON_US_LOCATION_FLAG;
  const signerStateAbbr = getStateAbbreviation(region);
  if (companyStateAbbr && signerStateAbbr && signerStateAbbr !== companyStateAbbr) {
    return `IP location (${signerStateAbbr}) doesn't match company state (${companyStateAbbr})`;
  }
  return null;
};

export type MiddeskTaskResult = {
  message?: string;
  status: string;
  label: string;
};

export type MiddeskSummary = {
  registration: any[];
  sosMatch: MiddeskTaskResult | null;
  nameMatch: MiddeskTaskResult | null;
  tinMatch: MiddeskTaskResult | null;
  tin: { name?: string; tin?: string };
};

/**
 * Maps a raw Middesk business-verification report (creditApplication.middeskReport.report)
 * into the SOS/EIN/business-name match results used across the app.
 */
export const getMiddeskSummary = (report: any): MiddeskSummary | null => {
  if (!report) return null;

  const mapTask = (
    tasks: any[],
    key: string,
    extraCondition?: (task: any) => boolean
  ): MiddeskTaskResult | null => {
    const task = tasks.find((t) => t.key === key && (!extraCondition || extraCondition(t)));

    return task
      ? {
          message: task.message,
          status: task.status,
          label: task.sub_label,
        }
      : null;
  };

  const registration = (report.registrations ?? []).map((reg: any) => ({
    addresses: reg.addresses ?? [],
    entityType: capitalizeWordsInSentence(reg.entity_type ?? ''),
    fileNumber: reg.file_number,
    jurisdiction: reg.jurisdiction ? capitalizeWordsInSentence(reg.jurisdiction) : '',
    name: reg.name,
    object: reg.object,
    officers: reg.officers,
    registeredAgent: reg.registered_agent,
    registrationDate: reg.registration_date,
    source: reg.source,
    state: reg.state ? getStateName(reg.state) : '',
    status: reg.status,
    statusDetails: reg.status_details ?? '-',
    subStatus: reg.sub_status ? capitalizeWordsInSentence(reg.sub_status.replace('_', ' ')) : '-',
  }));
  const tin = {
    name: report.tin?.name,
    tin: report.tin?.tin,
  };

  const tasks = report.review?.tasks ?? [];

  const sosMatch = mapTask(tasks, 'sos_match');
  const nameMatch = mapTask(tasks, 'name');
  const tinMatch = mapTask(tasks, 'tin', (t) => t.label === 'TIN Match');

  return {
    registration,
    sosMatch,
    nameMatch,
    tinMatch,
    tin,
  };
};

/**
 * Outside production there's no real Middesk report to summarize, so this
 * derives the same sample SOS/EIN match data from the applicant's own form
 * fields that PreviewCreditApplicationData shows inline (via getSamplSOS /
 * getSampleTin) — keeping the two "no real report yet" fallbacks consistent
 * instead of using unrelated static data.
 */
export const getMiddeskTestSummary = (
  formData: CreditApplicationFieldsValues[]
): MiddeskSummary | null => {
  const companyName = formData.find(
    (item) => item.id === 'customer-or-company-name' && item.value !== ''
  );
  const companyAddress = formData.find(
    (item) => item.id === 'company-address' && item.value !== ''
  );
  const companyCity = formData.find((item) => item.id === 'company-city' && item.value !== '');
  const companyState = formData.find((item) => item.id === 'company-state' && item.value !== '');
  const companyZip = formData.find((item) => item.id === 'company-zip' && item.value !== '');

  let address: { address?: string; city?: string; state?: string; zip?: string } = {};
  if (companyAddress?.value) address = { ...address, address: companyAddress.value as string };
  if (companyCity?.value) address = { ...address, city: companyCity.value as string };
  if (companyState?.value) address = { ...address, state: companyState.value as string };
  if (companyZip?.value) address = { ...address, zip: companyZip.value as string };

  const sosData = getSamplSOS(companyName?.value ?? '', address);

  const isEINItem = (item: { id: string; value?: unknown }) =>
    (item.id === 'company-ein' ||
      (item.id.includes('ein-number') && !item.id.includes('trade-reference'))) &&
    item.value !== '';
  const companyEIN =
    formData.find(isEINItem) ?? formData.flatMap((item) => item.fieldValue ?? []).find(isEINItem);
  const tinData = getSampleTin(
    (companyEIN?.value as string) ?? '',
    (companyName?.value as string) ?? ''
  );

  if (!sosData || !tinData) return null;
  return { ...sosData, ...tinData };
};

export type BureauScores = {
  creditSafe: { score: number; creditLimit: string } | null;
  experian: {
    intelliscore?: number;
    financialScoreRisk?: number;
    creditLimit?: string;
    fsrScoreTrends?: any[];
    commercialScoreTrends?: any[];
  } | null;
  nacm: { score: number | string } | null;
};

/**
 * Extracts the latest pulled score from each bureau's raw report blob.
 * Mirrors the per-bureau useMemos in CreditReport.tsx so both the full
 * "Credit Score & Reports" step and the compact Summary step read scores
 * the same way.
 */
export const getBureauScores = (pulledCreditReports?: any[] | null): BureauScores => {
  const reports = pulledCreditReports ?? [];

  const getTime = (item: any) =>
    item.updatedAt
      ? new Date(item.updatedAt).getTime()
      : item.createdAt
      ? new Date(item.createdAt).getTime()
      : 0;

  const latestBySource = (source: string) =>
    reports
      .filter((rpt) => rpt.source === source && rpt.type === 'business')
      .sort((a, b) => getTime(b) - getTime(a))[0];

  const creditSafeFirst = latestBySource('CreditSafe');
  let creditSafe: BureauScores['creditSafe'] = null;
  if (creditSafeFirst) {
    const score = (creditSafeFirst as any)?.data?.report?.companySummary?.creditRating
      ?.providerValue?.value;
    const creditLimit = (creditSafeFirst as any)?.data?.report?.companySummary?.creditRating
      ?.creditLimit?.value;
    if (score && creditLimit) {
      creditSafe = { score: Number(score), creditLimit: formatMoney(creditLimit, 2) };
    }
  }

  const experianFirst = latestBySource('Experian');
  let experian: BureauScores['experian'] = null;
  if (experianFirst) {
    const scoreInformation = (experianFirst as any)?.data?.results?.scoreInformation;
    const financialScoreRisk = scoreInformation?.fsrScore?.score;
    const intelliscore = scoreInformation?.commercialScore?.score;
    const recommendedCreditLimit = scoreInformation?.commercialScore?.recommendedCreditLimitAmount;
    if (financialScoreRisk || intelliscore) {
      experian = {
        intelliscore: intelliscore != null ? Number(intelliscore) : undefined,
        financialScoreRisk: financialScoreRisk != null ? Number(financialScoreRisk) : undefined,
        creditLimit:
          recommendedCreditLimit != null ? formatMoney(recommendedCreditLimit, 2) : undefined,
        fsrScoreTrends: scoreInformation?.fsrScoreTrends ?? [],
        commercialScoreTrends: scoreInformation?.commercialScoreTrends ?? [],
      };
    }
  }
  // Non-production environments often pull sandbox reports with no real score
  // attached. Fall back to a placeholder so the Experian card is visible for testing.
  if (!experian && process.env.REACT_APP_ENVIRONMENT !== 'production') {
    experian = {
      intelliscore: 664,
      creditLimit: formatMoney(4500, 2),
      fsrScoreTrends: [],
      commercialScoreTrends: [],
    };
  }

  const nacmFirst = latestBySource('NACM');
  let nacm: BureauScores['nacm'] = null;
  if (nacmFirst) {
    const score = (nacmFirst as any)?.data?.CmsCRScoreDetail?.Score;
    const isPlaceholderScore = process.env.REACT_APP_ENVIRONMENT !== 'production' && score == 0;
    if (score && !isPlaceholderScore) {
      nacm = { score };
    }
  }

  return { creditSafe, experian, nacm };
};
